/** In-app Help: searchable step-by-step guides, filtered to what the person can use. */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BookOpen, ChevronDown, Lightbulb } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { Card, PageHeader, filterClass } from '../../components/ui';
import { GUIDES } from './guides';

export function HelpPage(): JSX.Element {
  const { can, user } = useAuth();
  const [params] = useSearchParams();
  const topic = params.get('topic') ?? '';
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set(topic ? [topic] : []));

  useEffect(() => {
    if (!topic) return;
    setOpen((s) => new Set([...s, topic]));
    const t = window.setTimeout(() => document.getElementById(`guide-${topic}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 50);
    return () => window.clearTimeout(t);
  }, [topic]);

  const visible = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    return GUIDES.filter((g) => (g.grant ? Boolean(user?.grants?.includes(g.grant as never)) : !g.permissions || can(...(g.permissions as never[]))))
      .filter((g) => words.every((w) => [g.title, g.module, g.intro ?? '', ...g.steps, ...(g.tips ?? [])].join(' ').toLowerCase().includes(w)));
  }, [q, can, user]);
  const modules = [...new Set(visible.map((g) => g.module))];
  const toggle = (id: string) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title="Help" description="Step-by-step guides for the screens you can use. Tap ? at the top of any screen to jump to its guide."
        actions={<a href="/user-manual.pdf" target="_blank" rel="noreferrer" className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"><BookOpen aria-hidden="true" className="h-4 w-4" /> User manual (PDF)</a>} />
      <div className="mb-4">
        <input className={`${filterClass} w-full`} placeholder="Search help, e.g. add resident, summons, clearance" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search help" />
      </div>
      {!visible.length && <p className="text-sm text-slate-500">No guide matches “{q}”.</p>}
      <div className="space-y-6">
        {modules.map((m) => (
          <section key={m}>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">{m}</h2>
            <div className="space-y-2">
              {visible.filter((g) => g.module === m).map((g) => {
                const isOpen = open.has(g.id) || q.length > 1;
                return (
                  <Card key={g.id}>
                    <div id={`guide-${g.id}`} className="scroll-mt-20">
                      <button type="button" className="flex min-h-11 w-full items-center justify-between gap-3 text-left" aria-expanded={isOpen} onClick={() => toggle(g.id)}>
                        <span className="font-semibold text-slate-900 dark:text-white">{g.title}</span>
                        <ChevronDown aria-hidden="true" className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="mt-2 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                          {g.intro && <p className="mb-3">{g.intro}</p>}
                          <ol className="space-y-2">
                            {g.steps.map((s, i) => (
                              <li key={i} className="flex gap-3"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand text-xs font-bold text-white">{i + 1}</span><span>{s}</span></li>
                            ))}
                          </ol>
                          {g.tips && (
                            <div className="mt-3 rounded-xl bg-amber-50 p-3 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                              {g.tips.map((t) => <p key={t} className="flex gap-2"><Lightbulb aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />{t}</p>)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
